import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useConfig } from "@/hooks/useData";
import { usePrefs } from "@/context/PrefsContext";
import { DistrictLayers } from "@/components/cards/MapCard";

function Drawer({ onAdd }) {
  useMapEvents({ click(e) { onAdd([Number(e.latlng.lat.toFixed(6)), Number(e.latlng.lng.toFixed(6))]); } });
  return null;
}

const EMPTY = { name: "", color: "blue", polygon: [], unit_ids: [], hold_call_type_ids: [], notes: "", active: true };

export function DistrictEditor() {
  const distQ = useConfig("districts");
  const unitsQ = useConfig("units");
  const ctQ = useConfig("call_types");
  const { settings } = usePrefs();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const units = unitsQ.data || [];
  const callTypes = ctQ.data || [];
  const districts = (distQ.data || []).filter((d) => d.id !== draft?.id);

  useEffect(() => { if (!draft && distQ.data) setDraft({ ...EMPTY }); }, [distQ.data, draft]);

  const save = async () => {
    if (!draft.name.trim()) { toast.error("District name required"); return; }
    if (draft.polygon.length < 3) { toast.error("Draw at least 3 points on the map"); return; }
    setBusy(true);
    try {
      const body = { ...draft }; delete body.id;
      if (draft.id) await api.put(`/config/districts/${draft.id}`, body); else await api.post("/config/districts", body);
      qc.invalidateQueries({ queryKey: ["config", "districts"] }); qc.invalidateQueries({ queryKey: ["districts"] });
      toast.success("District saved"); setDraft({ ...EMPTY });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    try { await api.delete(`/config/districts/${d.id}`); qc.invalidateQueries({ queryKey: ["config", "districts"] }); qc.invalidateQueries({ queryKey: ["districts"] }); toast.success("Deleted"); } catch (e) { toast.error(errMsg(e)); }
  };
  const moveUnit = (idx, dir) => setDraft((d) => { const ids = [...d.unit_ids]; const j = idx + dir; if (j < 0 || j >= ids.length) return d; [ids[idx], ids[j]] = [ids[j], ids[idx]]; return { ...d, unit_ids: ids }; });
  if (!draft) return null;

  return (
    <div className="grid grid-cols-5 gap-3" data-testid="district-editor">
      <div className="col-span-3 space-y-2">
        <div className="flex items-center justify-between"><h2 className="display text-base font-semibold">Districts / beats</h2><span className="text-xs text-muted-foreground">Click the map to add polygon vertices. A polygon covers every address inside it.</span></div>
        <div className="h-[520px] rounded-xl overflow-hidden border">
          <MapContainer center={settings.map_center || [39.799, -89.65]} zoom={settings.map_zoom || 12} style={{ height: "100%" }}>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <DistrictLayers districts={districts} />
            {draft.polygon.length >= 3 ? <Polygon positions={draft.polygon} pathOptions={{ color: "#f59e0b", weight: 2, dashArray: "6 4", fillOpacity: 0.12 }} /> : <Polyline positions={draft.polygon} pathOptions={{ color: "#f59e0b", dashArray: "6 4" }} />}
            {draft.polygon.map((p, i) => <CircleMarker key={i} center={p} radius={5} pathOptions={{ color: "#f59e0b", fillOpacity: 1 }} />)}
            <Drawer onAdd={(pt) => setDraft((d) => ({ ...d, polygon: [...d.polygon, pt] }))} />
          </MapContainer>
        </div>
        <div className="flex gap-2 text-xs">
          <span className="mono text-muted-foreground self-center" data-testid="district-vertex-count">{draft.polygon.length} vertices</span>
          <button data-testid="district-undo-vertex" onClick={() => setDraft((d) => ({ ...d, polygon: d.polygon.slice(0, -1) }))} className="h-7 px-2 rounded border">Undo point</button>
          <button data-testid="district-clear-polygon" onClick={() => setDraft((d) => ({ ...d, polygon: [] }))} className="h-7 px-2 rounded border">Clear polygon</button>
        </div>
      </div>
      <div className="col-span-2 space-y-3">
        <div className="rounded-xl border bg-card p-3 space-y-2" data-testid="district-form">
          <div className="overline-label">{draft.id ? "Edit district" : "New district"}</div>
          <label className="block"><span className="overline-label">Name *</span><input data-testid="district-name-input" className="field-input mt-0.5" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
          <label className="block"><span className="overline-label">Color</span>
            <select data-testid="district-color-select" className="field-input mt-0.5" value={draft.color} onChange={(e) => setDraft({ ...draft, color: e.target.value })}>{["blue", "teal", "amber", "red", "green", "slate"].map((c) => <option key={c}>{c}</option>)}</select></label>
          <div>
            <span className="overline-label">Assigned units · rotation order (top = first)</span>
            <div className="mt-1 space-y-1">
              {draft.unit_ids.map((uid, i) => { const u = units.find((x) => x.id === uid); return (
                <div key={uid} className="flex items-center gap-1 text-xs rounded border px-2 py-1" data-testid={`district-unit-${u?.call_sign}`}>
                  <span className="mono font-semibold w-6">{i + 1}.</span><span className="mono font-semibold">{u?.call_sign || uid}</span><span className="text-muted-foreground truncate">{u?.description}</span>
                  <button onClick={() => moveUnit(i, -1)} className="ml-auto p-0.5 rounded hover:bg-accent"><ArrowUp className="h-3 w-3" /></button>
                  <button onClick={() => moveUnit(i, 1)} className="p-0.5 rounded hover:bg-accent"><ArrowDown className="h-3 w-3" /></button>
                  <button onClick={() => setDraft({ ...draft, unit_ids: draft.unit_ids.filter((x) => x !== uid) })} className="p-0.5 rounded hover:bg-accent text-red-500"><Trash2 className="h-3 w-3" /></button>
                </div>); })}
              <select data-testid="district-add-unit-select" className="field-input" value="" onChange={(e) => e.target.value && setDraft({ ...draft, unit_ids: [...draft.unit_ids, e.target.value] })}>
                <option value="">Add unit…</option>
                {units.filter((u) => !draft.unit_ids.includes(u.id)).map((u) => <option key={u.id} value={u.id}>{u.call_sign} · {u.discipline}</option>)}
              </select>
            </div>
          </div>
          <label className="block"><span className="overline-label">Hold rules — call types that hold for the district unit even when busy</span>
            <select data-testid="district-hold-select" multiple className="field-input !h-24 mt-0.5" value={draft.hold_call_type_ids} onChange={(e) => setDraft({ ...draft, hold_call_type_ids: [...e.target.selectedOptions].map((o) => o.value) })}>
              {callTypes.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.discipline})</option>)}
            </select></label>
          <label className="block"><span className="overline-label">Notes</span><input data-testid="district-notes-input" className="field-input mt-0.5" value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></label>
          <div className="flex justify-end gap-2">
            <button data-testid="district-reset" onClick={() => setDraft({ ...EMPTY })} className="h-8 px-3 rounded-md border text-xs">New / Reset</button>
            <button data-testid="district-save" disabled={busy} onClick={save} className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold">{draft.id ? "Save district" : "Create district"}</button>
          </div>
        </div>
        <div className="rounded-xl border overflow-hidden">
          <table className="w-full text-xs"><thead className="bg-muted/50 overline-label"><tr><th className="px-2 py-1.5 text-left">District</th><th className="px-2 py-1.5 text-left">Units (rotation)</th><th className="px-2 py-1.5 text-left">Next</th><th /></tr></thead>
            <tbody>{(distQ.data || []).map((d) => (
              <tr key={d.id} className={`border-t ${d.active === false ? "opacity-50" : ""}`} data-testid={`district-row-${d.id}`}>
                <td className="px-2 py-1 font-medium">{d.name}</td>
                <td className="px-2 py-1 mono">{(d.unit_ids || []).map((uid) => units.find((u) => u.id === uid)?.call_sign || "?").join(" → ")}</td>
                <td className="px-2 py-1 mono">#{((d.rotation_index || 0) % Math.max(1, (d.unit_ids || []).length)) + 1}</td>
                <td className="px-2 py-1 text-right whitespace-nowrap">
                  <button data-testid={`district-edit-${d.id}`} onClick={() => setDraft({ ...EMPTY, ...d })} className="text-primary hover:underline mr-2">Edit</button>
                  <button data-testid={`district-delete-${d.id}`} onClick={() => remove(d)} className="text-red-500 hover:underline">Delete</button>
                </td>
              </tr>))}</tbody></table>
        </div>
      </div>
    </div>
  );
}
